/**
 * Committee Approval DTO — Risk Committee Queue + Approval Detail.
 *
 * Distinct from `approval-request.dto.ts` (Issue #1559, BIC stream D):
 *
 *   - `ApprovalRequest`   — runtime gating for in-flight loan actions
 *     (e.g. "send term sheet" requires manager approval). Single approver
 *     pool, single decision, fast turnaround.
 *   - `CommitteeApproval` — formal risk-committee review of a loan with
 *     5+ voters, justifications, and historic decision context for trend
 *     analysis. Multi-day SLA, queue + heatmap UI.
 *
 * Wave 1 — DTO contract only. Wave 2 (29-case-management-service) owns
 * persistence; Wave 3 (10-business-portal Risk Committee Queue) renders
 * the queue + detail pane.
 */

import { z } from 'zod';

// ============================================================================
// Vote state + voter
// ============================================================================

/**
 * State of a single committee member's vote.
 *
 *   - `APPROVED`  — voter approved
 *   - `REJECTED`  — voter rejected (justification typically required)
 *   - `PENDING`   — voter has not yet voted
 *   - `ABSTAINED` — voter recused themselves (conflict of interest, etc.)
 */
export const CommitteeVoteStateSchema = z.enum(['APPROVED', 'REJECTED', 'PENDING', 'ABSTAINED']);
export type CommitteeVoteState = z.infer<typeof CommitteeVoteStateSchema>;

/**
 * A committee voter — denormalised display payload (we don't want the
 * portal to round-trip to account-service per voter per render). Backend
 * service is responsible for keeping these fields fresh.
 */
export const CommitteeVoterSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string().min(1).max(80),
  /** Role in this committee context (e.g. `CCO`, `Risk`, `Capital Mkts`). */
  role: z.string().min(1).max(80),
  avatarUrl: z.string().url().optional(),
  /**
   * Initials used as fallback for `InitialsBadge` when `avatarUrl` is
   * missing or fails to load (e.g. `RV`, `DM`). Max 3 chars to allow
   * three-initial names.
   */
  initials: z.string().min(1).max(3),
});
export type CommitteeVoterDto = z.infer<typeof CommitteeVoterSchema>;

// ============================================================================
// Vote
// ============================================================================

/**
 * A single voter's decision on a `CommitteeApproval`. `votedAt` is absent
 * while the vote is still `PENDING`.
 */
export const CommitteeVoteSchema = z.object({
  id: z.string().uuid(),
  approvalId: z.string().uuid(),
  voter: CommitteeVoterSchema,
  state: CommitteeVoteStateSchema,
  /** Optional justification text the voter attached. */
  justification: z.string().max(2000).optional(),
  /** ISO timestamp; absent while state = PENDING. */
  votedAt: z.string().datetime().optional(),
});
export type CommitteeVoteDto = z.infer<typeof CommitteeVoteSchema>;

// ============================================================================
// Decision history (heatmap row)
// ============================================================================

/**
 * One historic committee decision, used to drive the decision-history
 * heatmap below the detail pane. The heatmap colors each cell by `outcome`
 * and surfaces an "avg time to decide" stat from `daysToDecide`.
 */
export const DecisionHistoryEntrySchema = z.object({
  id: z.string().uuid(),
  caseId: z.string().uuid(),
  /** Final outcome — drives the heatmap cell color. */
  outcome: z.enum(['APPROVED', 'DENIED', 'WITHDRAWN']),
  /**
   * Days from queue-entry to decision. Non-negative; same-day decisions
   * are `0` (UI shows "<1 day"). Optional because the underlying column is
   * nullable for rows backfilled from before filed-date provenance existed.
   * Consumers averaging this value (#3237461077) MUST filter out missing
   * entries — treating them as 0 silently underreports the displayed avg.
   */
  daysToDecide: z.number().min(0).optional(),
  decidedAt: z.string().datetime(),
});
export type DecisionHistoryEntryDto = z.infer<typeof DecisionHistoryEntrySchema>;

// ============================================================================
// Loan summary + Astor block (detail-pane subblocks)
// ============================================================================

/**
 * Loan summary block at the top of the detail pane (loan ID, requested
 * amount, LTARV, routing, vote-round-of indicator).
 */
export const CommitteeApprovalLoanSummarySchema = z.object({
  /** Human-readable loan identifier (not a UUID — e.g. `EXC-142`). */
  loanId: z.string().min(1).max(60),
  /** Requested loan amount in whole dollars (cents stored elsewhere). */
  requestedAmount: z.number().int().min(0),
  /** LTARV as a percent (e.g. 72.5). Capped at 200 to allow unusual structures. */
  ltarv: z.number().min(0).max(200),
  /** Committee that owns the decision (e.g. `Risk Committee`). */
  routedTo: z.string().min(1).max(80),
  /**
   * Current vote round `[round, totalRounds]` (e.g. `[1, 5]` = round 1 of 5
   * required rounds before final decision). Both values must be ≥ 1.
   */
  voteRoundOf: z.tuple([z.number().int().min(1), z.number().int().min(1)]),
});
export type CommitteeApprovalLoanSummaryDto = z.infer<typeof CommitteeApprovalLoanSummarySchema>;

/**
 * Astor's pre-screen block — surfaced at the top of the detail pane so
 * voters see the agent's framing before they review the loan summary.
 */
export const CommitteeApprovalAstorBlockSchema = z.object({
  summary: z.string().min(1).max(1000),
  mitigants: z.array(z.string().min(1)).default([]),
});
export type CommitteeApprovalAstorBlockDto = z.infer<typeof CommitteeApprovalAstorBlockSchema>;

// ============================================================================
// Root — committee approval
// ============================================================================

/**
 * Urgency tier driving the queue-row dot color + sort order:
 *
 *   - `NORMAL` — gray dot, default SLA
 *   - `HIGH`   — amber dot, escalated
 *   - `URGENT` — red dot, near-SLA-breach or already breached
 */
export const CommitteeUrgencySchema = z.enum(['NORMAL', 'HIGH', 'URGENT']);
export type CommitteeUrgency = z.infer<typeof CommitteeUrgencySchema>;

/**
 * Approval-level status. Recomputed atomically inside CastCommitteeVote:
 *   - any REJECTED                → REJECTED
 *   - all non-abstained APPROVED  → APPROVED
 *   - otherwise                    → still PENDING
 *
 * Surfaced on the DTO (#3237461052) so the risk-committee queue + downstream
 * consumers can filter "open vs resolved" without re-deriving from the votes
 * array. The router's `status` filter input maps directly to this field.
 */
export const CommitteeApprovalStatusSchema = z.enum(['PENDING', 'APPROVED', 'REJECTED']);
export type CommitteeApprovalStatus = z.infer<typeof CommitteeApprovalStatusSchema>;

export const CommitteeApprovalSchema = z.object({
  id: z.string().uuid(),
  caseId: z.string().uuid(),
  /** Title shown at top of the detail pane (e.g. `EXC-142: Borrower experience shortfall`). */
  title: z.string().min(1).max(200),
  urgency: CommitteeUrgencySchema,
  /**
   * Days remaining in SLA. Can be negative ("3 days overdue" → -3) so the
   * queue can sort and color overdue items appropriately.
   */
  slaDaysRemaining: z.number().int(),
  /** ISO timestamp when the approval was filed. UI shows "filed 4 days ago". */
  filedAt: z.string().datetime(),
  /**
   * Current lifecycle state. PENDING until the committee reaches consensus
   * (or one voter REJECTs); transitions to APPROVED / REJECTED atomically
   * via the conditional-update guard in CommitteeApprovalService.castVote.
   */
  status: CommitteeApprovalStatusSchema,
  /** ISO timestamp when status flipped out of PENDING. Null while PENDING. */
  resolvedAt: z.string().datetime().nullable(),
  loanSummary: CommitteeApprovalLoanSummarySchema,
  /** Astor's pre-screen markup — optional when Astor was bypassed. */
  astorBlock: CommitteeApprovalAstorBlockSchema.optional(),
  /** Committee voters and current state. */
  votes: z.array(CommitteeVoteSchema),
});
export type CommitteeApprovalDto = z.infer<typeof CommitteeApprovalSchema>;
